import { useState } from 'react';
import { PartnerEndpoints } from '../../../utils/endpoints';
import { Permissions } from '../../../utils/permissions';
import { Constants } from '../../../utils/constants';
import { formatCount } from '../../../utils/money';
import { useAuth } from '../../../AuthContext';
import PageHeader from '../../shell/PageHeader';
import {
  Alert,
  DataTable,
  DateRangeFilter,
  ExportButton,
  FilterSelect,
  Pagination,
  SearchInput,
  TableCard,
} from '../../ui';
import { useServerTable } from '../../../hooks/useServerTable';
import { useExport } from '../../../hooks/useExport';
import tapTransactionColumns from './dashboard/tapTransactionColumns';
import useOwnPans from './sub-merchant/useOwnPans';
import PanSelect from './sub-merchant/PanSelect';
import SubMerchantDeactivated, { isSubMerchantDeactivated } from './sub-merchant/SubMerchantDeactivated';

const breadcrumbs = [{ label: 'Home', to: '/layout' }, { label: 'Transactions' }];

const SubMerchantTransactions = () => {
  const { hasPermission } = useAuth();
  const pans = useOwnPans({ enabled: hasPermission(Permissions.subMerchantDashboard) });
  const multiplePans = pans.length > 1;
  const [pan, setPan] = useState('');
  const canExport = hasPermission(Permissions.subMerchantTransactionsExport);
  const exporter = useExport(PartnerEndpoints.ownTransactionsExport, 'We could not export your transactions. Please try again.');

  const table = useServerTable(PartnerEndpoints.ownTransactions, {
    sort: '-created_at',
    filters: { status: '' },
    extraParams: { pan },
    fallbackError: 'We could not load your transactions. Please try again.',
  });

  if (isSubMerchantDeactivated(table.requestError)) {
    return (
      <>
        <PageHeader title="Transactions" breadcrumbs={breadcrumbs} />
        <SubMerchantDeactivated />
      </>
    );
  }

  const columns = tapTransactionColumns({
    showPan: multiplePans,
    dateFilter: (
      <DateRangeFilter
        label="Date"
        from={table.from}
        to={table.to}
        onFromChange={table.setFrom}
        onToChange={table.setTo}
      />
    ),
    statusFilter: (
      <FilterSelect
        value={table.filters.status ?? ''}
        onChange={(value) => table.setFilter('status', value)}
        options={Constants.transactionStatuses}
        label="All"
        ariaLabel="Filter by status"
      />
    ),
  });

  return (
    <>
      <PageHeader title="Transactions" breadcrumbs={breadcrumbs} />
      {exporter.error && (
        <Alert variant="danger" onDismiss={exporter.clearError} className="mb-6">
          {exporter.error}
        </Alert>
      )}
      <TableCard
        title="Transactions"
        summary={{ label: 'Transactions', value: formatCount(table.total), loading: table.loading }}
        toolbar={
          <>
            {multiplePans && <PanSelect pans={pans} value={pan} onChange={setPan} />}
            <SearchInput value={table.search} onChange={table.setSearch} placeholder="Search reference or payer" />
            {canExport && (
              <ExportButton
                onClick={() => exporter.runExport(table.params)}
                loading={exporter.exporting}
                disabled={table.loading || table.total === 0 || table.invalidDateRange}
                title={`Exports all ${formatCount(table.total)} matching transactions, not just this page`}
              />
            )}
          </>
        }
        footer={
          table.total > 0 && (
            <Pagination
              page={table.page}
              pageCount={table.pageCount}
              pageSize={table.pageSize}
              onPageChange={table.setPage}
              onPageSizeChange={table.setPageSize}
            />
          )
        }
      >
        <DataTable
          columns={columns}
          rows={table.rows}
          rowKey={(row) => row.id}
          serial
          serialStart={table.page * table.pageSize}
          loading={table.loading}
          error={table.error}
          invalidDateRange={table.invalidDateRange}
          sort={table.sort}
          onSortChange={table.setSort}
          empty={{ title: 'No transactions', message: 'No transactions found for the selected filters.' }}
        />
      </TableCard>
    </>
  );
};

export default SubMerchantTransactions;
